import useDeleteVersion from 'api/versionControl/useDeleteVersion/useDeleteVersion';
import useEditVersion from 'api/versionControl/useEditVersion/useEditVersion';
import useGetVersionEditInfo from 'api/versionControl/useGetVersionEditInfo/useGetVersionEditInfo';
import useInsertVersion from 'api/versionControl/useInsertVersion/useInsertVersion';
import useVersionList from 'api/versionControl/useVersionList/useVersionList';
import VersionControlEditModal from 'components/private/Modals/versionControl/VersionControlEditModal/VersionControlEditModal';
import VersionControlInsertModal from 'components/private/Modals/versionControl/VersionControlInsertModal/VersionControlInsertModal';
import DeleteModal from 'components/shared/DeleteModal/DeleteModal';
import ApiPaginateTable from 'components/shared/Table/ApiPaginateTable';
import { permissions } from 'hooks/permissions/permissions';
import { usePermissions } from 'hooks/permissions/usePermissions';
import useVersionControlTableColumns from 'hooks/versionControl/useVersionControlTableColumns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import PageSpinner from '../../components/shared/pageSpinner/PageSpinner';

// last search string
let lastSearch = null;

const VersionControlPage = () => {
  const { guid } = useParams();
  const { can } = usePermissions();

  const versionsPermissions =
    permissions.general.categories.keySettings.basicSettings.versionControl
      .versions;

  const canCreate = can(versionsPermissions.create);
  const canDelete = can(versionsPermissions.delete);
  const canEdit = can(versionsPermissions.edit);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState('100');
  const [getVersionList, loading, versionList, pageRef] = useVersionList();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteChosenVersion, setDeleteChosenVersion] = useState({});
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editChosenVersion, setEditChosenVersion] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [isInsertModalOpen, setIsInsertModalOpen] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);

  const [deleteRequest] = useDeleteVersion();
  const [editRequest] = useEditVersion();
  const [getVersionEditInfo, editInfoLoading, versionEditInfo] =
    useGetVersionEditInfo();
  const [insertRequest] = useInsertVersion();

  const tableRef = useRef(null);

  const fetchVersionList = useCallback(
    (params = {}) => {
      getVersionList({
        Guid: guid,
        Page: params.page ?? page,
        Count: params.count ?? Number(pageSize),
        ...(params.term !== undefined ? { term: params.term } : {}),
      });
    },
    [getVersionList, guid, page, pageSize],
  );

  useEffect(() => {
    if (guid) {
      fetchVersionList({ page: 1, count: pageSize });
    }
  }, [guid]);

  useEffect(() => {
    if (guid) {
      fetchVersionList({ page, count: pageSize });
    }
  }, [pageSize]);

  const deleteItem = useCallback(
    row => () => {
      setDeleteChosenVersion(row);
      setIsDeleteModalOpen(true);
    },
    [],
  );
  const editItem = useCallback(
    row => () => {
      setEditChosenVersion(row);
      getVersionEditInfo(row.id);
      setIsEditModalOpen(true);
    },
    [getVersionEditInfo],
  );

  const downloadItem = useCallback(
    row => () => {
      window.open(row.fileUrl, '_blank');
    },
    [],
  );

  const [columnsData] = useVersionControlTableColumns(
    deleteItem,
    editItem,
    downloadItem,
    tableRef,
    {
      canEdit,
      canDelete,
    },
  );

  const deleteHandle = id => {
    setDeleteLoading(true);
    deleteRequest(id, fetchVersionList, setIsDeleteModalOpen, setDeleteLoading);
  };

  const editHandle = payload => {
    setEditLoading(true);
    editRequest(
      payload,
      () => fetchVersionList({ page, count: pageSize }),
      setIsEditModalOpen,
      setEditLoading,
    );
  };

  const insertHandle = payload => {
    setInsertLoading(true);
    insertRequest(
      () => fetchVersionList({ page, count: pageSize }),
      setIsInsertModalOpen,
      setInsertLoading,
      payload,
    );
  };

  function getPageData(pageNumber) {
    setPage(pageNumber);
    fetchVersionList({
      page: pageNumber,
      count: pageSize,
      term: lastSearch,
    });
  }

  function handleSearch(text) {
    setPage(1);
    lastSearch = text;
    if (text === '') {
      lastSearch = null;
    }
    fetchVersionList({ page: 1, count: pageSize, term: text });
  }

  return (
    <>
      <div>{pageRef.refTitle}</div>

      {Object.keys(versionList).length !== 0 ? (
        <ApiPaginateTable
          columns={columnsData}
          rowsData={versionList[page]}
          rows={versionList[page]}
          pageSize={pageSize}
          isDeletable={canDelete}
          isLoading={loading}
          addLable={canCreate ? 'اضافه کردن نسخه جدید' : undefined}
          onAddClick={canCreate ? () => setIsInsertModalOpen(true) : undefined}
          setPageSize={val => setPageSize(val)}
          apiRef={tableRef}
          onPageChange={p => getPageData(p)}
          page={page}
          countPages={
            pageRef?.totalCount ? Math.ceil(pageRef.totalCount / pageSize) : 1
          }
          handleSearchApi={handleSearch}
          setSelectedRows={rows => setSelectedRows(rows)}
          selectedRows={selectedRows}
        />
      ) : (
        <PageSpinner loading />
      )}

      {canDelete ? (
        <DeleteModal
          open={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          locationId={deleteChosenVersion.id}
          title={deleteChosenVersion.title}
          deleteLoading={deleteLoading}
          onDelete={deleteHandle}
        />
      ) : null}

      {canEdit ? (
        <VersionControlEditModal
          open={isEditModalOpen}
          handleClose={() => setIsEditModalOpen(false)}
          applicationGuid={guid}
          initialValues={versionEditInfo}
          versionGuid={editChosenVersion.id}
          editLoading={editLoading || editInfoLoading}
          onEdit={editHandle}
        />
      ) : null}

      {canCreate ? (
        <VersionControlInsertModal
          open={isInsertModalOpen}
          handleClose={() => setIsInsertModalOpen(false)}
          applicationGuid={guid}
          insertLoading={insertLoading}
          onInsert={insertHandle}
        />
      ) : null}
    </>
  );
};

export default VersionControlPage;
